import { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useT } from '_metronic/i18n/Metronici18n'
import { CampoResolvido, RespostaEnvio } from '../formularios-create/core/_models'
import { montarContexto } from '../core/condicao/montarContexto'
import { CampoFormularioBase } from '../core/condicao/esquema'
import { CampoPreenchimento, ValorCampo } from './CampoPreenchimento'

export type ValoresPreenchimento = Record<string, ValorCampo>

type Props = {
  /** `id` do `<form>`, para um botão fora dele enviar (atributo `form`). */
  id?: string
  campos: CampoResolvido[]
  /** Variáveis do atendimento resolvidas pelo servidor (ou simuladas na pré-visualização). */
  variaveis?: Record<string, string | number>
  valoresIniciais?: ValoresPreenchimento
  /**
   * O que a requisição de busca trouxe do servidor, por código de campo.
   *
   * É a **base de comparação** do envio: só vai no corpo o campo cujo valor atual difere daqui.
   * Não confundir com `valoresIniciais`, que pode ser um rascunho local — usar o rascunho como base
   * faria o envio omitir edições que o usuário já tinha feito.
   *
   * Quando ausente, cai em `valoresIniciais` (pré-visualização, onde não há busca).
   */
  valoresGravados?: ValoresPreenchimento
  /** Textos de apoio por código de campo (ex.: "Resposta anterior: …"). */
  ajudas?: Record<string, string>
  /** Erros vindos do servidor, por código de campo. */
  errosExternos?: Record<string, string>
  somenteLeituraGeral?: boolean
  enviando?: boolean
  /**
   * Ponto de montagem de um aviso não bloqueante acima das perguntas.
   *
   * É por aqui que o `FormularioModal` monta o aviso de respostas apagadas por referência ilegível
   * (T8c). A lista de campos zerados vem do servidor — o renderizador não infere nada no cliente.
   */
  aviso?: ReactNode
  onSubmit?: (respostas: RespostaEnvio[]) => void | Promise<void>
  onValoresChange?: (valores: ValoresPreenchimento) => void
  renderAcoes?: (estado: { enviando: boolean }) => ReactNode
}

const vazio = (campo: CampoResolvido): ValorCampo =>
  campo.tipo === 'SELECT' && campo.multiplo ? [] : ''

const estaVazio = (valor: ValorCampo | undefined): boolean =>
  valor === undefined || (Array.isArray(valor) ? valor.length === 0 : valor.trim() === '')

/** Normaliza para lista: `undefined` e `''` viram `[]`, para comparar sem falso positivo. */
const comoLista = (valor: ValorCampo | undefined): string[] => {
  if (Array.isArray(valor)) {
    return valor
  }
  return valor === undefined || valor === '' ? [] : [valor]
}

/** Igualdade de conteúdo, sem depender da ordem (campo múltiplo). */
const mesmoValor = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) {
    return false
  }
  const ordenadoA = [...a].sort()
  const ordenadoB = [...b].sort()
  return ordenadoA.every((valor, indice) => valor === ordenadoB[indice])
}

const paraContexto = (campos: CampoResolvido[]): CampoFormularioBase[] =>
  campos.map((campo) => ({
    id: typeof campo.id === 'number' ? campo.id : undefined,
    codigo: campo.codigo,
    tipo: campo.tipo,
    multiplo: campo.multiplo,
    ordem: campo.ordem,
    opcoes: campo.opcoes,
    condicaoExibicao: campo.condicaoExibicao ?? null,
  }))

/**
 * Renderizador de preenchimento reutilizável (atendimento, pré-visualização e, depois, rota do
 * paciente da T3). Não fala com a API: recebe os campos já prontos e devolve as respostas.
 *
 * A visibilidade é recalculada a cada digitação **só para UX** — o backend revalida no envio.
 * Campo invisível não é renderizado nem enviado, mesmo que já tenha valor digitado.
 */
export const FormularioPreenchimento: FC<Props> = ({
  id,
  campos,
  variaveis,
  valoresIniciais,
  valoresGravados,
  ajudas,
  errosExternos,
  somenteLeituraGeral,
  enviando,
  aviso,
  onSubmit,
  onValoresChange,
  renderAcoes,
}) => {
  const { t } = useT()

  const [valores, setValores] = useState<ValoresPreenchimento>(() => {
    const iniciais: ValoresPreenchimento = {}
    campos.forEach((campo) => {
      iniciais[campo.codigo] = valoresIniciais?.[campo.codigo] ?? vazio(campo)
    })
    return iniciais
  })
  const [tocados, setTocados] = useState<Record<string, boolean>>({})
  const [tentouEnviar, setTentouEnviar] = useState(false)
  const focos = useRef<Record<string, HTMLElement | null>>({})

  const onValoresChangeRef = useRef(onValoresChange)
  onValoresChangeRef.current = onValoresChange

  useEffect(() => {
    onValoresChangeRef.current?.(valores)
  }, [valores])

  // Cada campo registra o contêiner do componente do sistema; o foco vai ao primeiro controle dele.
  const focar = (codigo: string) =>
    focos.current[codigo]
      ?.querySelector<HTMLElement>('input, textarea, select, [contenteditable="true"]')
      ?.focus()

  // Erro devolvido pelo servidor (ex.: RESPOSTAS_INVALIDAS) leva o foco ao primeiro campo citado.
  useEffect(() => {
    const primeiro = Object.keys(errosExternos ?? {})[0]
    if (primeiro) focar(primeiro)
  }, [errosExternos])

  const { visiveis } = useMemo(
    () => montarContexto(paraContexto(campos), valores, variaveis),
    [campos, valores, variaveis]
  )

  const camposVisiveis = useMemo(
    () =>
      visiveis
        .map((codigo) => campos.find((campo) => campo.codigo === codigo))
        .filter((campo): campo is CampoResolvido => !!campo),
    [visiveis, campos]
  )

  const errosObrigatorios = useMemo(() => {
    const erros: Record<string, string> = {}
    camposVisiveis.forEach((campo) => {
      if (campo.obrigatorio && estaVazio(valores[campo.codigo])) {
        erros[campo.codigo] = t('formularios.preenchimento.obrigatorio')
      }
    })
    return erros
  }, [camposVisiveis, valores, t])

  const erroDoCampo = (codigo: string): string | undefined => {
    if (errosExternos?.[codigo]) return errosExternos[codigo]
    if (!tentouEnviar && !tocados[codigo]) return undefined
    return errosObrigatorios[codigo]
  }

  /**
   * Só o que o usuário alterou (decisão da rodada 19).
   *
   * Campo não tocado não vai no corpo. A comparação é contra o que a **busca trouxe**, nunca contra
   * "o campo está vazio": resposta que a tela não soube desenhar (opção excluída ou renomeada) abre
   * vazia e continuaria parecendo "alterada para vazio", e o servidor apagaria registro clínico que
   * o usuário nem chegou a ver. Enviando só o alterado, "não enviado" volta a significar "ninguém
   * tocou" — que é o que o servidor sempre assumiu.
   */
  const respostas = useCallback((): RespostaEnvio[] => {
    const base = valoresGravados ?? valoresIniciais
    return camposVisiveis
      .map((campo) => ({ campo, lista: comoLista(valores[campo.codigo]) }))
      .filter(({ campo, lista }) => !mesmoValor(lista, comoLista(base?.[campo.codigo])))
      .map(({ campo, lista }) => ({ codigo: campo.codigo, valores: lista }))
  }, [camposVisiveis, valores, valoresGravados, valoresIniciais])

  const handleSubmit = async (evento: React.FormEvent) => {
    evento.preventDefault()
    setTentouEnviar(true)
    const primeiroInvalido = camposVisiveis.find((campo) => errosObrigatorios[campo.codigo])
    if (primeiroInvalido) {
      focar(primeiroInvalido.codigo)
      return
    }
    await onSubmit?.(respostas())
  }

  return (
    <form id={id} onSubmit={handleSubmit} noValidate>
      {aviso}
      {camposVisiveis.length === 0 && (
        <p className='text-muted fs-7'>{t('formularios.preenchimento.vazio')}</p>
      )}
      {camposVisiveis.map((campo) => (
        <CampoPreenchimento
          key={campo.codigo}
          campo={campo}
          valor={valores[campo.codigo] ?? vazio(campo)}
          erro={erroDoCampo(campo.codigo)}
          ajuda={ajudas?.[campo.codigo]}
          disabled={somenteLeituraGeral || enviando}
          registrarFoco={(elemento) => {
            focos.current[campo.codigo] = elemento
          }}
          onBlur={() => setTocados((anterior) => ({ ...anterior, [campo.codigo]: true }))}
          onChange={(valor) => setValores((anterior) => ({ ...anterior, [campo.codigo]: valor }))}
        />
      ))}
      {renderAcoes?.({ enviando: !!enviando })}
    </form>
  )
}
